import { useMemo, useState } from 'react';
import {
  CallCard,
  CallsView,
  LogCallModal,
  callingStore,
  useToast,
  type CallFilter,
  type CallRow,
} from '@elva/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useClearPageTopBarActions } from '@/app/layouts/pageTopBar';

const TYPE_FILTERS: CallFilter[] = ['all', 'asked', 'treatment', 'invisalign', 'balance', 'recall'];

/**
 * Calls (spec 31) — one list for every call a person makes. `/calls` and
 * `/calls?type=…` (agent pages link here with the filter set). The card opens
 * from Call (calling state) or a row click (not calling); Log the call opens the
 * modal, whose Save shows a toast and, on mock, closes.
 */
export function CallsPage() {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();

  const filter: CallFilter = useMemo(() => {
    const t = params.get('type');
    return t !== null && TYPE_FILTERS.includes(t as CallFilter) ? (t as CallFilter) : 'all';
  }, [params]);

  const [openRow, setOpenRow] = useState<CallRow | null>(null);
  const [calling, setCalling] = useState(false);
  const [logRow, setLogRow] = useState<CallRow | null>(null);

  const setFilter = (f: CallFilter) => {
    if (f === 'all') setParams({});
    else setParams({ type: f });
  };

  return (
    <>
      <CallsView
        filter={filter}
        onFilter={setFilter}
        callingBy={callingStore}
        onCall={(row) => { setOpenRow(row); setCalling(true); }}
        onOpenRow={(row) => { setOpenRow(row); setCalling(false); }}
        onPrint={() => { window.print(); }}
      />

      <CallCard
        row={openRow}
        calling={calling}
        callingSince="10:42 am"
        onClose={() => { setOpenRow(null); setCalling(false); }}
        onStartCall={() => { setCalling(true); }}
        onLogCall={(row) => { setLogRow(row); }}
        onOpenPatient={(row) => { void navigate(`/patients/${row.id}/profile`); }}
        onOpenReason={(_row, href) => { void navigate(href); }}
        onOpenCommunications={(row) => { void navigate(`/patients/${row.id}/communications`); }}
      />

      <LogCallModal
        open={logRow !== null}
        row={logRow}
        by="Ava Thompson, 10:42 – 10:49 am"
        onClose={() => { setLogRow(null); }}
        onSave={(row) => { setLogRow(null); setOpenRow(null); setCalling(false); toast({ title: `Logged — ${row.name}`, variant: 'success' }); }}
        onStopCalls={(row) => { setLogRow(null); setOpenRow(null); setCalling(false); toast({ title: `Calls stopped for ${row.name}`, variant: 'warning' }); }}
      />
    </>
  );
}
